import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useAuthApi } from "../hooks/useAuthApi";
import { MGMT } from "../env";
import type { Contract, Driver, Plan } from "../types";
import { fullDriversFromResponse, type DriverListResponse } from "../driverResponses";
import { useToast } from "../toast";

interface Props {
  open: boolean;
  contract: Contract | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function ContractDialog({ open, contract, onClose, onSaved }: Props) {
  const api = useAuthApi();
  const [name, setName] = useState("");
  const [planId, setPlanId] = useState<number | "">("");
  const [driverId, setDriverId] = useState<number | "">("");
  const [codeUsed, setCodeUsed] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [validFrom, setValidFrom] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [saving, setSaving] = useState(false);

  const [plans, setPlans] = useState<Plan[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);

  const toast = useToast();

  const loadOptions = useCallback(async () => {
    try {
      const [p, d] = await Promise.all([
        api<Plan[]>(`${MGMT}/plans`),
        api<DriverListResponse>(`${MGMT}/drivers`),
      ]);
      setPlans(p ?? []);
      setDrivers(fullDriversFromResponse(d));
    } catch (e) {
      toast.show((e as Error).message, "error");
    }
  }, [toast]);

  useEffect(() => {
    if (!open) return;
    loadOptions();
    if (contract) {
      setName(contract.name ?? "");
      setPlanId(contract.plan_id);
      setDriverId(contract.driver_id);
      setCodeUsed(contract.code_used ?? "");
      setIsActive(contract.is_active);
      setValidFrom(contract.valid_from ? contract.valid_from.slice(0, 10) : "");
      setValidUntil(contract.valid_until ? contract.valid_until.slice(0, 10) : "");
    } else {
      setName("");
      setPlanId("");
      setDriverId("");
      setCodeUsed("");
      setIsActive(true);
      setValidFrom("");
      setValidUntil("");
    }
  }, [open, contract, loadOptions]);

  const selectedPlan = plans.find((p) => p.id === planId) ?? null;

  const handleSave = async () => {
    if (planId === "" || driverId === "") {
      toast.show("Select both a plan and a driver", "error");
      return;
    }
    setSaving(true);
    const body = {
      name: name.trim() || undefined,
      plan_id: planId as number,
      driver_id: driverId as number,
      code_used: codeUsed.trim().toUpperCase() || undefined,
      is_active: isActive,
      valid_from: validFrom || undefined,
      valid_until: validUntil || undefined,
    };

    try {
      if (contract) {
        await api(`${MGMT}/contracts/${contract.id}`, {
          method: "PATCH",
          body: JSON.stringify({ name: body.name, is_active: body.is_active, valid_from: body.valid_from, valid_until: body.valid_until }),
        });
        toast.show("Contract updated");
      } else {
        await api(`${MGMT}/contracts`, {
          method: "POST",
          body: JSON.stringify(body),
        });
        toast.show("Contract created");
      }
      onSaved();
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setSaving(false);
    }
  };

  const chargerLabel = (plan: Plan) => {
    if (plan.charger_config) return plan.charger_config.display_name ?? plan.charger_config.charge_point_id;
    if (plan.charger_group) return `Group: ${plan.charger_group.name}`;
    return "—";
  };

  const tariffLabel = (plan: Plan) => {
    if (plan.tariff) return `${plan.tariff.name} (${plan.tariff.currency})`;
    return "Charger default";
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{contract ? "Edit Contract" : "New Contract"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Name (optional)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
          />

          <Divider>
            <Typography variant="caption" color="text.secondary">Plan</Typography>
          </Divider>

          <FormControl fullWidth required disabled={!!contract}>
            <InputLabel>Plan</InputLabel>
            <Select
              value={planId}
              label="Plan"
              onChange={(e) => setPlanId(e.target.value as number)}
            >
              {plans.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}{p.requires_code ? " 🔑" : ""}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedPlan && (
            <Box sx={{ bgcolor: "action.hover", borderRadius: 1, p: 1.5 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Charger: <strong>{chargerLabel(selectedPlan)}</strong>
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Tariff: <strong>{tariffLabel(selectedPlan)}</strong>
              </Typography>
            </Box>
          )}

          <Divider>
            <Typography variant="caption" color="text.secondary">Driver</Typography>
          </Divider>

          <FormControl fullWidth required disabled={!!contract}>
            <InputLabel>Driver</InputLabel>
            <Select
              value={driverId}
              label="Driver"
              onChange={(e) => setDriverId(e.target.value as number)}
            >
              {drivers.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.name} ({d.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {!contract && selectedPlan?.requires_code && (
            <TextField
              label="Access Code"
              value={codeUsed}
              onChange={(e) => setCodeUsed(e.target.value.toUpperCase())}
              helperText="Required by this plan — leave empty only if creating as admin without code validation"
              fullWidth
              inputProps={{ style: { fontFamily: "monospace", letterSpacing: 2 } }}
            />
          )}

          <Divider>
            <Typography variant="caption" color="text.secondary">Validity</Typography>
          </Divider>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Valid From"
              type="date"
              value={validFrom}
              onChange={(e) => setValidFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Valid Until"
              type="date"
              value={validUntil}
              onChange={(e) => setValidUntil(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Stack>

          <FormControlLabel
            control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
            label="Active"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {contract ? "Save" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
